import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { ObjectSchema, boolean, mixed, number, object, string } from "yup";
import { AvisConstants } from "../../../../data/avisConstants";
import {
  CreationInfo,
  CreationsListFilter,
  ItemViewModel,
} from "../../../../generated/data-contracts";
import { useSessionStorage } from "../../../../hooks/useSessionStorage";
import {
  CreationListMode,
  CreationStatuses,
  FilterType,
  ILinkValue,
} from "../../../../models";
import { AuthorsUtils, IAxiosError, TableUtils, api } from "../../../../utils";
import { AvisForm } from "../../../shared/components";
import { AvisDescriptiveText } from "../../../shared/components/form";
import {
  AvisFilterableTable,
  IColumnConfig,
  ITableColumnProps,
} from "../../../shared/components/table";
import { AvisContext } from "../../../shared/context/AvisContext";
import { CreationsFilterForm } from "./filter/CreationsFilterForm";

interface ICreationsPageProps {
  creationListMode?: CreationListMode;
  defaultCreationTypeId?: number;
  selectedCreation?: CreationInfo | undefined;
  setSelectedCreation?: React.Dispatch<
    React.SetStateAction<CreationInfo | undefined>
  >;
}

export function CreationsPage(props: Readonly<ICreationsPageProps>) {
  const {
    creationListMode = CreationListMode.Regular,
    selectedCreation,
    defaultCreationTypeId,
    setSelectedCreation,
  } = props;
  const { ctxRightsHolder, res } = useContext(AvisContext);
  const defaultFilterValues: CreationsListFilter = useMemo(() => {
    const defaultFilter: CreationsListFilter = {
      creationClassId: defaultCreationTypeId,
      creationNameFilterType: "Contains",
      creationName: "",
      creationStatusId: undefined,
      forUnidentifiedCreations:
        creationListMode === CreationListMode.UnidentifiedPicker,
      pageIndex: 0,
      pageSize: 10,
    };
    return defaultFilter;
  }, [creationListMode, defaultCreationTypeId]);
  const [creationClassesSession, setCreationClassesSession] = useSessionStorage(
    AvisConstants.creations_filter_classes_key,
  );
  const [creationStatusesSession, setCreationStatusesSession] =
    useSessionStorage(
      creationListMode === CreationListMode.Regular
        ? AvisConstants.creations_filter_statuses_key
        : AvisConstants.creations_filter_statuses_unidentfied_key,
    );

  const [creationClasses, setCreationClasses] = useState<
    ItemViewModel[] | undefined
  >(creationClassesSession);
  const [creationStatusList, setCreationStatusList] = useState<
    ItemViewModel[] | undefined
  >(creationStatusesSession);

  const [error, setError] = useState<IAxiosError>();

  const [isCreationClassesLoading, setIsCreationClassesLoading] =
    useState<boolean>(false);
  const [isCreationStatusListLoading, setIsCreationStatusListLoading] =
    useState<boolean>(false);

  const getCreationClasses = useCallback(() => {
    if (creationClasses || isCreationClassesLoading) return;
    setIsCreationClassesLoading(true);

    api
      .classifiersGetCreationClassesForFilter()
      .then((res) => {
        setCreationClasses(res.data);
        setCreationClassesSession(res.data);
      })
      .catch(setError)
      .finally(() => setIsCreationClassesLoading(false));
  }, [creationClasses, isCreationClassesLoading, setCreationClassesSession]);

  const getCreationStatusList = useCallback(() => {
    if (creationStatusList || isCreationStatusListLoading) return;

    setIsCreationStatusListLoading(true);
    const filterApi =
      creationListMode === CreationListMode.Regular
        ? api.classifiersGetCreationStatusListForFilter()
        : api.classifiersGetCreationStatusListForFilterForUnidentified();

    filterApi
      .then((res) => {
        setCreationStatusList(res.data);
        setCreationStatusesSession(res.data);
      })
      .catch(setError)
      .finally(() => setIsCreationStatusListLoading(false));
  }, [
    creationStatusList,
    isCreationStatusListLoading,
    creationListMode,
    setCreationStatusesSession,
  ]);

  useEffect(() => {
    getCreationStatusList();
  }, [getCreationStatusList]);

  useEffect(() => {
    getCreationClasses();
  }, [getCreationClasses]);

  const validationSchema: ObjectSchema<CreationsListFilter> = object({
    creationClassId: number(),
    creationName: string(),
    creationNameFilterType: mixed<FilterType>(),
    creationStatusId: number(),
    forUnidentifiedCreations: boolean(),
    pageIndex: number().min(0),
    pageSize: number().min(1),
    searchAllNames: boolean(),
  });

  const creativeProfileColumnTemplate = useCallback(
    (rowData: any, column: ITableColumnProps): JSX.Element => {
      if (!column.field || !rowData.creationId) return <></>;

      const showProfileCount = 2;
      const fieldData = rowData[column.field];

      if (!fieldData || !Array.isArray(fieldData) || fieldData.length === 0)
        return <></>;

      const profilesWithName = fieldData
        .filter(
          (profile) =>
            profile.creationRoleName?.length > 0 ||
            profile.fullName?.length > 0,
        )
        .map((profile) => {
          return {
            creationId: profile.creationId,
            profileAndName: [profile.creationRoleName, profile.fullName]
              .filter((x) => !!x && x.length > 0)
              .join(" "),
          };
        });

      const firstNProfilesData = profilesWithName.slice(0, showProfileCount);

      // Shown profiles
      const firstProfiles = firstNProfilesData.map((profile, i) => {
        return (
          <div
            key={`${profile.creationId}_${i}`}
            className="creations-profile-record table-cell-value-overflow"
            title={profile.profileAndName}
          >
            {profile.profileAndName}
          </div>
        );
      });

      // More profiles
      let moreProfiles = undefined;
      if (fieldData.length > showProfileCount) {
        const moreProfilesTitle = profilesWithName
          .slice(showProfileCount, fieldData.length)
          .map((profile) => profile.profileAndName)
          .join("\n");

        moreProfiles = (
          <div className="creations-profile-record table-cell-value-overflow">
            <span title={moreProfilesTitle}>
              {res("authors_Creation_label_AndMore")}
            </span>
          </div>
        );
      }

      return (
        <div>
          {firstProfiles}
          {moreProfiles}
        </div>
      );
    },
    [res],
  );

  const onSelectedItemChange = useMemo(
    () => (rowData: CreationInfo, checked: boolean) => {
      if (!setSelectedCreation) return;

      setSelectedCreation(checked ? rowData : undefined);
    },
    [setSelectedCreation],
  );

  const columnMetadata: IColumnConfig[] = useMemo(() => {
    return [
      ...(creationListMode === CreationListMode.UnidentifiedPicker
        ? [
            TableUtils.columnConfigCheckbox<CreationInfo>(
              "creationId",
              " ",
              [],
              selectedCreation?.creationId
                ? [selectedCreation?.creationId]
                : [],
              onSelectedItemChange,
            ),
          ]
        : []),
      TableUtils.columnConfigLink<CreationInfo>(
        "name",
        res("authors_CreationsPage_TableColumn_Name"),
        (rowData) => {
          if (!rowData.creationId) return;

          return {
            title: rowData.name,
            url: AuthorsUtils.getRightsHolderPagePath(
              ctxRightsHolder,
              `${
                AvisConstants.url_part_creations
              }/${rowData.creationClassCode?.toLowerCase()}/${btoa(
                rowData.creationId?.toString(),
              )}`,
            ),
            target:
              creationListMode === CreationListMode.UnidentifiedPicker
                ? "_blank"
                : undefined,
          } as ILinkValue;
        },
        120,
      ),
      TableUtils.columnConfigString<CreationInfo>(
        "number",
        res("authors_CreationsPage_TableColumn_CreationNumber"),
        undefined,
        75,
      ),
      TableUtils.columnConfig<CreationInfo>(
        "creativeProfiles",
        res("authors_Creation_label_CreativeProfiles"),
        "Object",
        creativeProfileColumnTemplate,
      ),
      TableUtils.columnConfigString<CreationInfo>(
        "creationClassName",
        res("authors_CreationsPage_TableColumn_CreationClassName"),
      ),
      TableUtils.columnConfigString<CreationInfo>(
        "statusNamePortal",
        res("authors_CreationsPage_TableColumn_Status"),
        undefined,
        120,
      ),
    ];
  }, [
    creationListMode,
    selectedCreation?.creationId,
    onSelectedItemChange,
    res,
    creativeProfileColumnTemplate,
    ctxRightsHolder,
  ]);
  const customRowClassNames = (rowData: CreationInfo): string => {
    switch (rowData.statusNamePortal) {
      case CreationStatuses.FilledOnline:
      case CreationStatuses.Conflict:
      case CreationStatuses.Rejected:
        return "avis-creation-highlighted";
      default:
        return "";
    }
  };
  const renderContent = useMemo(() => {
    if (!creationClasses || !creationStatusList || !ctxRightsHolder) return;

    return (
      <>
        <AvisDescriptiveText
          textKey={
            creationListMode == CreationListMode.Regular
              ? "portal_RH_Creations_1"
              : "portal_RH_CreationSearch"
          }
        />
        <AvisFilterableTable<CreationInfo, CreationsListFilter>
          defaultFilterValues={defaultFilterValues}
          filterSessionKey={
            creationListMode === CreationListMode.Regular
              ? AvisConstants.creations_filter_values_key
              : AvisConstants.creations_search_filter_values_key
          }
          filterValidationSchema={validationSchema}
          customRowClassNames={customRowClassNames}
          setError={setError}
          getApiData={(filter) =>
            api.rightsHoldersGetCreationsList(
              ctxRightsHolder.rightsHolderId,
              filter as any,
            )
          }
          renderFilter={(bag, loading, filtered) => (
            <CreationsFilterForm
              bag={bag}
              loading={loading}
              creationClasses={creationClasses}
              creationStatusList={creationStatusList}
              defaultCreationTypeId={defaultCreationTypeId}
              filtered={filtered}
            />
          )}
          forceUpdateKey={selectedCreation}
          tableColumns={columnMetadata}
          tableColumnWidths={
            creationListMode === CreationListMode.Regular
              ? [30, 10, 20, 15, 15]
              : [5, 30, 15, 20, 15, 15]
          }
        />
      </>
    );
  }, [
    columnMetadata,
    creationClasses,
    creationListMode,
    creationStatusList,
    ctxRightsHolder,
    defaultCreationTypeId,
    defaultFilterValues,
    selectedCreation,
    validationSchema,
  ]);

  return (
    <AvisForm
      title={res("authors_CreationsPage_Title")}
      error={error}
      loading={isCreationClassesLoading || isCreationStatusListLoading}
    >
      {renderContent}
    </AvisForm>
  );
}
